import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEOMeta from "@/components/base/SEOMeta";
import { getArticleBySlug, articles, categories } from "@/mocks/articles";

const relatedArticleImages: Record<string, string> = {
  "home-security-guide-2026": "https://readdy.ai/api/search-image?query=A%20professional%20modern%20minimalist%20scene%20related%20to%20security%20and%20safety%20concept%2C%20clean%20composition%2C%20soft%20natural%20lighting%2C%20warm%20neutral%20tones%2C%20editorial%20photography%20style%2C%20high%20detail%2C%20professional%20atmosphere&width=600&height=350&seq=article-home-guide-rel&orientation=landscape",
  "business-security-risk-assessment": "https://readdy.ai/api/search-image?query=A%20modern%20corporate%20meeting%20room%20with%20security%20presentation%20on%20screen%2C%20professional%20business%20setting%2C%20clean%20office%20environment%2C%20soft%20lighting%2C%20corporate%20atmosphere%2C%20editorial%20photography%20style%2C%20high%20detail&width=600&height=350&seq=article-business-risk-rel&orientation=landscape",
  "personal-safety-essentials": "https://readdy.ai/api/search-image?query=A%20minimalist%20desk%20with%20personal%20safety%20devices%20arranged%20neatly%2C%20compact%20alarm%20tools%2C%20modern%20lifestyle%20photography%2C%20soft%20window%20light%2C%20clean%20neutral%20background%2C%20professional%20product%20display&width=600&height=350&seq=article-personal-safety-rel&orientation=landscape",
  "smart-home-security-trends": "https://readdy.ai/api/search-image?query=A%20futuristic%20smart%20home%20with%20AI%20security%20interface%20hologram%2C%20modern%20living%20room%2C%20clean%20design%2C%20soft%20ambient%20lighting%2C%20technology%20concept%2C%20editorial%20photography%2C%20high%20detail%2C%20warm%20tones&width=600&height=350&seq=article-smart-trends-rel&orientation=landscape",
  "office-fire-safety-guide": "https://readdy.ai/api/search-image?query=A%20modern%20office%20with%20fire%20safety%20equipment%20displayed%20professionally%2C%20clean%20corporate%20environment%2C%20safety%20signage%2C%20bright%20natural%20lighting%2C%20professional%20photography%20style%2C%20warm%20neutral%20atmosphere&width=600&height=350&seq=article-fire-safety-rel&orientation=landscape",
  "night-walk-safety-tips": "https://readdy.ai/api/search-image?query=A%20safe%20well%20lit%20city%20street%20at%20night%20with%20streetlamps%2C%20modern%20urban%20setting%2C%20warm%20lighting%2C%20clean%20cityscape%2C%20safety%20concept%2C%20editorial%20photography%2C%20professional%20atmosphere%2C%20high%20detail&width=600&height=350&seq=article-night-safety-rel&orientation=landscape",
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.id === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      "@type": "Organization",
      name: "安大师",
      url: "https://www.anmaster.com/",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.anmaster.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${article.title} - 安大师`}
        description={article.excerpt}
        keywords={`安大师,${article.tags.join(",")},${article.categoryName}`}
        canonicalUrl={`https://www.anmaster.com/article/${article.slug}`}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading mt-6 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读 {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-price-tag-3-line"></i>
                {article.categoryName}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:font-heading prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-900 font-heading mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {relatedArticles.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/50 overflow-hidden transition-all duration-300 hover:border-primary-300/50"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={relatedArticleImages[item.slug] || relatedArticleImages["home-security-guide-2026"]}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 font-heading line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">{item.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
              {categoryInfo && (
                <div className="text-center mt-8">
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap"
                  >
                    查看更多{article.categoryName}文章
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}